import { useEffect, useState, useCallback, useRef } from 'react';
import { isMaintenanceEnabled, isMaintenanceForced, subscribeToMaintenanceMode } from '../maintenanceUtils';

/**
 * Returns true if current Montreal time is between 00:00 and 04:59.
 * Uses Intl.DateTimeFormat with America/Toronto to handle DST correctly.
 */
function isMontrealMaintenanceWindow(): boolean {
  try {
    const hourStr = new Intl.DateTimeFormat('en-CA', {
      hour: 'numeric',
      hour12: false,
      timeZone: 'America/Toronto',
    }).format(new Date());
    const hour = parseInt(hourStr, 10);
    return hour >= 0 && hour < 5;
  } catch {
    // Fallback: use toLocaleString
    const now = new Date();
    const montrealTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Toronto' }));
    const h = montrealTime.getHours();
    return h >= 0 && h < 5;
  }
}

/**
 * Reactive hook that combines:
 * - Automatic maintenance: 00:00–05:00 Montreal time
 * - Forced maintenance: app_settings.maintenance_mode.enabled === true
 *
 * Re-checks time every 30s and subscribes to realtime DB changes.
 * Returns { isMaintenance: boolean, isLoading: boolean }
 */
export function useMaintenanceMode() {
  const [timeBased, setTimeBased] = useState(() => isMontrealMaintenanceWindow());
  const [dbBased, setDbBased] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const mountedRef = useRef(true);

  // Check time periodically
  useEffect(() => {
    const check = () => {
      if (mountedRef.current) setTimeBased(isMontrealMaintenanceWindow());
    };
    const id = setInterval(check, 30_000);
    return () => clearInterval(id);
  }, []);

  // Check DB + subscribe to realtime changes
  useEffect(() => {
    mountedRef.current = true;

    (async () => {
      try {
        const enabled = await isMaintenanceEnabled();
        if (mountedRef.current) setDbBased(enabled);
      } catch {
        // keep false
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    })();

    const cleanup = subscribeToMaintenanceMode((enabled) => {
      if (mountedRef.current) setDbBased(enabled);
    });

    return () => {
      mountedRef.current = false;
      try { cleanup(); } catch { /* noop */ }
    };
  }, []);

  return {
    isForced: isMaintenanceForced() || dbBased,
    isMaintenance: isMaintenanceForced() || timeBased || dbBased,
    isLoading,
  };
}

/**
 * Non-hook version for use outside React components (e.g. in route guards).
 * Checks time only (synchronous). DB check requires async.
 */
export { isMontrealMaintenanceWindow };
